/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactElement, useState, useContext, useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Button, Badge } from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import CreateReceiverAccountModal from "./CreateReceiverAccountModal";
import UpdateReceiverAccountModal from "./UpdateReceiverAccountModal";
import ReceiverAccountDetailsModal from "../../shared/ReceiverAccountDetailsModal";
import { Tooltip, IconButton } from "@mui/material";
import { AiOutlineEdit, AiOutlineInfoCircle } from "react-icons/ai";
import { MdOutlineContentCopy } from "react-icons/md";
import SweetAlert2 from "react-sweetalert2";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { BsFillSendCheckFill } from "react-icons/bs";
import ResendAccountConfirmModal from "./ResendAccountConfirmModal";
import { ProfileContext } from "../../../../store/ProfileProvider";
import AgGridPagination from "../../shared/AgGridPagination";

dayjs.extend(utc);
dayjs.extend(timezone);

ModuleRegistry.registerModules([AllCommunityModule]);

type RowData = {
  id: string;
  created_at: string;
  asset: string;
  method_name: string;
  confirm_status: string;
};

const ReceiverAccountList: React.FC<{
  userPermissions: any;
  isLoading: boolean;
  tableData: RowData[];
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
  currentLastKey: string;
  receiverId: string;
  triggerReloadReceiverAccountList(): void;
}> = ({
  userPermissions,
  isLoading,
  tableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel,
  currentLastKey,
  receiverId,
  triggerReloadReceiverAccountList,
}) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  const [
    isOpenCreateReceiverAccountModal,
    setIsOpenCreateReceiverAccountModal,
  ] = useState<boolean>(false);
  const [
    isOpenUpdateReceiverAccountModal,
    setIsOpenUpdateReceiverAccountModal,
  ] = useState<boolean>(false);
  const [receiverAccountDetailToUpdate, setReceiverAccountDetailToUpdate] =
    useState<any>(null);

  const [
    isOpenReceiverAccountDetailModal,
    setIsOpenReceiverAccountDetailModal,
  ] = useState<boolean>(false);
  const [receiverAccountDetailToDisplay, setReceiverAccountDetailToDisplay] =
    useState<any>(null);

  const [isOpenResendAccountConfirmModal, setIsOpenResendAccountConfirmModal] =
    useState<boolean>(false);
  const [
    receiverAccountDetailForResendConfirm,
    setReceiverAccountDetailForResendConfirm,
  ] = useState(null);

  const [swalProps, setSwalProps] = useState<any>({});

  interface CustomerStatusItems {
    status: string;
    value: string;
    display: ReactElement;
  }

  const statusClasses = "status";

  const statusList: CustomerStatusItems[] = [
    {
      status: "unconfirmed",
      value: "unconfirmed",
      display: (
        <Badge className={`${statusClasses} status-unconfirmed`}>
          Unconfirmed
        </Badge>
      ),
    },
    {
      status: "sent_mail",
      value: "sent_mail",
      display: (
        <Badge className={`${statusClasses} status-sent_mail`}>
          Email Sent
        </Badge>
      ),
    },
    {
      status: "confirmed",
      value: "confirmed",
      display: (
        <Badge className={`${statusClasses} status-confirmed`}>Confirmed</Badge>
      ),
    },
  ];

  const handleClickCopy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch (err) {
      console.error("Failed to copy ID: ", err);
    }
  };

  const formatDate = (timestamp: string) => {
    return dayjs(Number(timestamp))
      .tz(profileTimeZone)
      .format("DD MMM YY - hh:mm A");
  };

  const CustomLoadingOverlay = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <CircularProgress size={40} />
    </div>
  );

  const CopyIdRenderer = (params: ICellRendererParams) => (
    <Tooltip title="Copy ID" arrow placement="top">
      <div>
        <IconButton
          id={"button-copy-id-" + params.data?.id}
          onClick={() => handleClickCopy(params.data.id)}
          size="small"
        >
          <MdOutlineContentCopy className="h-5 w-5 text-navy-700" />
        </IconButton>
      </div>
    </Tooltip>
  );

  const CreatedDateRenderer = (params: ICellRendererParams) => (
    <>{formatDate(params.data.created_at)}</>
  );

  const StatusRenderer = (params: ICellRendererParams) => {
    const customerStatus = params.data.confirm_status;
    const statusItem = statusList.find(
      (item) => item.status === customerStatus
    );
    return <>{statusItem ? statusItem.display : null}</>;
  };

  const ActionsRenderer = (params: ICellRendererParams) => {
    return (
      <>
        {params.data?.confirm_status === "sent_mail" &&
          userRole === "superadmin" && (
            <Tooltip title="Resend Confirmation Link" arrow placement="top">
              <IconButton
                id={"button-resend-account-confirm-" + params.data?.id}
                onClick={() => handleResendAccount(params.data)}
                size="small"
              >
                <BsFillSendCheckFill className="h-5 w-5 text-navy-700 opacity-90" />
              </IconButton>
            </Tooltip>
          )}
        <Tooltip title="Details" arrow placement="top">
          <IconButton
            id={"button-details-" + params.data?.id}
            onClick={() => handleClickDetails(params.data)}
            size="small"
          >
            <AiOutlineInfoCircle className="h-5 w-5 text-navy-700" />
          </IconButton>
        </Tooltip>
      </>
    );
  };

  const columns: ColDef[] = [
    {
      field: "id",
      headerName: "",
      width: 50,
      sortable: false,
      resizable: false,
      cellRenderer: CopyIdRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "created_at",
      headerName: "Created Date",
      sortable: false,
      minWidth: 175,
      flex: 1,
      cellRenderer: CreatedDateRenderer,
    },
    {
      field: "asset",
      headerName: "Asset",
      sortable: false,
      width: 80,
    },
    {
      field: "method_name",
      headerName: "Method",
      sortable: false,
      minWidth: 80,
      flex: 1,
    },
    {
      field: "confirm_status",
      headerName: "Confirm Status",
      sortable: false,
      width: 140,
      cellRenderer: StatusRenderer,
      cellClass: "ag-cell-status",
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      width: 140,
      resizable: false,
      cellRenderer: ActionsRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
    },
  ];

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  const showAddAccountModal = () => {
    setIsOpenCreateReceiverAccountModal(true);
  };

  const closeAddAccountModal = () => {
    setIsOpenCreateReceiverAccountModal(false);
  };

  const handleClickUpdate = (receiverAccount: any) => {
    setReceiverAccountDetailToUpdate(receiverAccount);
    showUpdateAccountModal();
  };

  const showUpdateAccountModal = () => {
    setIsOpenUpdateReceiverAccountModal(true);
  };

  const closeUpdateAccountModal = () => {
    setReceiverAccountDetailToUpdate(null);
    setIsOpenUpdateReceiverAccountModal(false);
  };

  const handleClickDetails = (receiverAccount: any) => {
    setReceiverAccountDetailToDisplay(receiverAccount);
    showAccountDetailsModal();
  };

  const showAccountDetailsModal = () => {
    setIsOpenReceiverAccountDetailModal(true);
  };

  const closeAccountDetailsModal = () => {
    setReceiverAccountDetailToDisplay(null);
    setIsOpenReceiverAccountDetailModal(false);
  };

  const triggerReloadListForUpdate = () => {
    setReceiverAccountDetailToUpdate(null);
    triggerReloadReceiverAccountList();
  };

  const handleResendAccount = (receiverAccount: any) => {
    setReceiverAccountDetailForResendConfirm(receiverAccount);
    showResendAccountConfirmModal();
  };

  const showResendAccountConfirmModal = () => {
    setIsOpenResendAccountConfirmModal(true);
  };

  const closeResendAccountConfirmModal = () => {
    setReceiverAccountDetailForResendConfirm(null);
    setIsOpenResendAccountConfirmModal(false);
  };

  const triggerReloadListForResendConfirm = () => {
    setReceiverAccountDetailForResendConfirm(null);
    triggerReloadReceiverAccountList();
  };

  const hidePagination = currentLastKey === "" && page === 0;

  return (
    <div className="p-8">
      {receiverId !== null ? (
        <>
          {isOpenCreateReceiverAccountModal && (
            <CreateReceiverAccountModal
              setSwalProps={setSwalProps}
              triggerReloadReceiverAccountList={triggerReloadReceiverAccountList}
              receiverId={receiverId}
              isOpen={isOpenCreateReceiverAccountModal}
              onClose={closeAddAccountModal}
            />
          )}
          {isOpenUpdateReceiverAccountModal && receiverAccountDetailToUpdate && (
            <UpdateReceiverAccountModal
              setSwalProps={setSwalProps}
              triggerReloadReceiverAccountList={triggerReloadListForUpdate}
              receiverAccountDetail={receiverAccountDetailToUpdate}
              isOpen={isOpenUpdateReceiverAccountModal}
              onClose={closeUpdateAccountModal}
            />
          )}
          {isOpenResendAccountConfirmModal && receiverAccountDetailForResendConfirm && (
            <ResendAccountConfirmModal
              setSwalProps={setSwalProps}
              triggerReload={triggerReloadListForResendConfirm}
              receiverAccountToResendConfirm={
                receiverAccountDetailForResendConfirm
              }
              isOpen={isOpenResendAccountConfirmModal}
              onClose={closeResendAccountConfirmModal}
            />
          )}
          {isOpenReceiverAccountDetailModal && receiverAccountDetailToDisplay && (
            <ReceiverAccountDetailsModal
              detail={receiverAccountDetailToDisplay}
              isOpen={isOpenReceiverAccountDetailModal}
              onClose={closeAccountDetailsModal}
            />
          )}
        </>
      ) : null}

      {userPermissions?.addReceiverAccount && (
        <div className="mb-3 flex items-center justify-end">
          <Button
            isDisabled={isLoading}
            onClick={() => showAddAccountModal()}
            className="btn btn-action !px-6"
          >
            <FaPlus />
            &nbsp;Add New Account
          </Button>
        </div>
      )}
      <Box>
        <div style={{ width: "100%" }}>
          <AgGridReact
            theme={themeAlpine}
            rowData={isLoading ? [] : tableData}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            getRowId={(params) => params.data.id}
            rowHeight={48}
            headerHeight={44}
            domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
            suppressPaginationPanel={true}
            loading={isLoading}
            loadingOverlayComponent={CustomLoadingOverlay}
            overlayNoRowsTemplate="No data available"
          />
        </div>

        <AgGridPagination
          page={page}
          pageSize={pageSize}
          rowCount={rowCount}
          onPaginationChange={setPaginationModel}
          hidden={hidePagination}
        />
      </Box>

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </div>
  );
};

export default ReceiverAccountList;
